import { FieldError, RegisterOptions, useFormContext } from 'react-hook-form';

import InfoCircleIcon from 'assets/icons/infoCircle.svg';
import { FormGroupModifier } from 'models/form';
import {
  ChangeEvent, useEffect, useRef, useState,
} from 'react';
import useModifiers from 'hooks/useModifiers';
import CloseIcon from 'assets/icons/close.svg';

export enum TextfieldModifier {
  NARROW = 'narrow',
  INTEGRATION = 'integration',
  OVERVIEW = 'overview',
}

export interface FormTextfieldProps {
  name: string;
  options?: RegisterOptions;
  label?: string;
  placeholder?: string;
  tooltip?: string;
  debounceMs?: number;
  modifiers?: TextfieldModifier[];
  clearable?: boolean;
}

function FormTextfield(props: FormTextfieldProps) {
  const mods = useModifiers(props.modifiers);
  const formContext = useFormContext();
  const [isActive, setIsActive] = useState(false);
  const [valueState, setValueState] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { errors } = formContext.formState;
  const {
    name, options, label = '', tooltip, placeholder, debounceMs = 0, clearable = false,
  } = props;

  const error = errors && errors[name] as FieldError;
  const hasError = Boolean(error);

  const isDisabled = options && options.disabled;

  const hasErrorMod = hasError ? FormGroupModifier.HAS_ERROR : '';
  const disabledMod = isDisabled ? FormGroupModifier.DISABLED : '';
  const activeMod = isActive ? FormGroupModifier.ACTIVE : '';

  // custom onChange with debounce possibility
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { value } = e.nativeEvent.target as HTMLInputElement;

    setValueState(value);
  };

  useEffect(() => {
    if (valueState !== null) {
      const handler = () => {
        formContext.setValue(name, valueState);

        // needed for validation
        formContext.trigger(name)
          .then(() => { })
          .catch((e) => console.log(e));
      };

      if (debounceMs && debounceMs > 0) {
        timerRef.current = setTimeout(() => handler(), debounceMs);
      } else {
        handler();
      }
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [valueState]);

  return (
    <div className={`ct_form-group ${hasErrorMod} ${disabledMod} ${activeMod}`}>
      {!!label && (
      <div className="ct_form-desc">
        <div className="ct_form-label">{label}</div>
        {tooltip && (
        /* TODO: Implement Tooltip Component  */
        <div className="ct_form-info">
          <InfoCircleIcon />
          {tooltip}
        </div>
        )}
      </div>
      )}
      <div className="ct_form-control">
        <input
          className={`ct_textfield ${mods} ${clearable && valueState ? '_hasButton' : ''}`}
          type="text"
          {...formContext.register(name, { ...options })}
          placeholder={placeholder}
          onFocus={() => setIsActive(true)}
          onBlur={() => setIsActive(false)}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
        />
        {!!clearable && !!valueState && (
          <button type="button" onClick={() => setValueState('')} className="ct_textfield-btn _clear">
            <CloseIcon />
          </button>
        )}
      </div>
      {hasError && error && (
        <div className="ct_form-msg">
          Error:
          {error.type || ''}
        </div>
      )}
    </div>
  );
}

FormTextfield.defaultProps = { ...{} };

export default FormTextfield;
